import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import i18next from "i18next"
import { useLanguages } from "lang/languages"
import { useDialogs } from "modules/overlays"
import { useSite } from "modules/sites"
import { UserContext } from "modules/users"

export const handleResponse = (response) => {
  if (response && (response.Result === 0 || response.result === 0)) {
    const message = `${response.error_code || response.MessageCode || response.messageCode || response.Message || ""}`
    if (response.MessageCodeParams) throw Error(message, { cause: response.MessageCodeParams })
    throw Error(message)
  }
  return Promise.resolve(response || { Data: {}, data: {} })
}

const fetchJSON = (url, options) => {
  const hostName = window.location.host.split(".")[0]
  let client
  if (hostName.includes("dashboard")) {
    client = "sales_dashboard"
  } else {
    client = "sales_portal"
  }
  return fetch(url, {
    credentials: "include",
    ...options,
    headers: { ...(options?.headers || {}), "Go-Client": client },
  }).then((response) => {
    if (!response.ok) {
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("json")) {
        return response.json().then((error) => Promise.reject(error))
      } else {
        return response.text().then((message) => Promise.reject({ message, status: response.status }))
      }
    }
    if (response.headers.get("content-length") === "0") {
      return
    }
    if (response.headers.get("content-type")?.includes("json")) {
      return response.json()
    }
    return
  })
}

const buildFetcher =
  (method) =>
  (url, data, options = {}) =>
    fetchJSON(url, {
      method,
      body: JSON.stringify(data),
      ...options,
      headers: Object.assign(
        // upload request would assign options.body, it must leave content-type header for browser to handle
        options.body ? {} : { "Content-Type": "application/json" },
        options.headers,
      ),
    })

export const getJSON = (url, params, options) => {
  const query = params && typeof params === "object" ? JSON.parse(JSON.stringify(params)) : {} // remove undefined values from params
  return fetchJSON(`${url}${Object.keys(query).length ? "?" + new URLSearchParams(query).toString() : ""}`, options)
}

export const postJSON = buildFetcher("POST")

export const putJSON = buildFetcher("PUT")

export const deleteJSON = buildFetcher("DELETE")

export const useFetch = (hostName) => {
  const controllerRef = useRef()
  const { user, setUser } = useContext(UserContext)
  const { clientCode } = useSite()
  const { language } = useLanguages()
  const { openErrorModal } = useDialogs()

  const sessionId = user?.sessionId || user?.SessionId

  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort()
      }
    }
  }, [])

  const handleFetch = useCallback(
    (request, path, data, options = {}) => {
      controllerRef.current = new AbortController()
      const headers = Object.assign(
        sessionId
          ? {
              "Go-Client": clientCode,
              "X-Session-Id": sessionId,
            }
          : {},
        options.headers,
        { "Accept-Language": `${language};q=1.0` },
      )
      return request(`${hostName || ""}${path}`, data, {
        credentials: "include",
        signal: controllerRef.current.signal,
        ...options,
        headers,
      }).catch((error) => {
        if (error.name === "AbortError") {
          return console.log(error)
        }
        switch (error.status) {
          case 401:
            return (
              [
                "/api/auth/logout",
                "/api/utility/account/logout",
                "/tw/api/account/logout",
                "/api/utility/account/get-current-user",
                "/tw/api/account/get-current-user",
              ].includes(path)
                ? Promise.resolve()
                : openErrorModal({ message: i18next.t("sales:error.not_log_in_or_be_logged_out") })
            ).then(() =>
              // user context may not exist yet in some cases
              typeof setUser === "function" ? setUser(null) : Promise.reject(error),
            )
          case 504:
            return openErrorModal({ message: "Request Timed Out" })
          default:
            return Promise.reject(error)
        }
      })
    },
    [hostName, language, openErrorModal, setUser, sessionId, clientCode],
  )

  const getData = useCallback((path, data, options) => handleFetch(getJSON, path, data, options), [handleFetch])
  const postData = useCallback((path, data, options) => handleFetch(postJSON, path, data, options), [handleFetch])
  const putData = useCallback((path, data, options) => handleFetch(putJSON, path, data, options), [handleFetch])
  const deleteData = useCallback((path, data, options) => handleFetch(deleteJSON, path, data, options), [handleFetch])

  return { getData, postData, putData, deleteData }
}

export const usePaginator = (node) => {
  const initialPagination = useMemo(
    () => ({
      pageNumber: 0,
      nextPage: 0,
      totalPages: Infinity,
    }),
    [],
  )
  const [loading, setLoading] = useState(false)
  const paginationRef = useRef({ ...initialPagination })
  const loadingRef = useRef()
  const fetcherRef = useRef()

  const registerFetcher = useCallback((fetcher) => {
    fetcherRef.current = fetcher
  }, [])

  const resetPaginator = useCallback(
    (pagination) => {
      paginationRef.current = Object.assign({}, initialPagination, pagination)
    },
    [initialPagination],
  )

  const fetchNextPage = useCallback(() => {
    if (
      !loadingRef.current &&
      typeof fetcherRef.current === "function" &&
      paginationRef.current.nextPage &&
      paginationRef.current.pageNumber !== paginationRef.current.totalPages
    ) {
      setLoading(true)
      return fetcherRef
        .current(paginationRef.current)
        .then((pagination) => (paginationRef.current = pagination))
        .finally(() => setLoading(false))
    }
  }, [])

  const fetchFirstPage = useCallback(() => {
    paginationRef.current.nextPage = 1
    return fetchNextPage()
  }, [fetchNextPage])

  useEffect(() => {
    // need to use ref to avoid fetchNextPage depending on loading
    loadingRef.current = loading
  }, [loading])

  useEffect(() => {
    if (node) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && paginationRef.current && paginationRef.current.pageNumber) {
          fetchNextPage()
        }
      })
      observer.observe(node)
      return () => observer.unobserve(node)
    }
  }, [node, fetchNextPage])

  return {
    registerFetcher,
    fetchFirstPage,
    fetchNextPage,
    resetPaginator,
    loading,
    pagination: paginationRef.current,
  }
}
